import { track } from '@/lib/analytics/track';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import type { ButtonEventProps } from '@/models/analytics/features/ui';
import { ButtonAction, ScreenName } from '@heidi/runtime/analytics-vocabulary';

/**
 * Nav-v3 interactions reuse the generic `button_clicked` event (published Iglu
 * schema, registered Snowplow handler) — the specifics travel in `label` /
 * `click_source` / ids, per the analytics-validator "existing event + props"
 * rule, so no new event name or schema change is needed.
 *
 * `screen_name`/`action` default to `ScreenName.Sidenav` /
 * `ButtonAction.NavV3ButtonClicked` — the same `screen_name` the main sidebar's
 * `CreateButton` reports, and one generic action shared by every nav-v3 call
 * site since `label` already disambiguates the specific control (mirrors
 * `create-button.tsx`'s `screen_name`/`action` pair). A caller may override
 * either when it needs to.
 *
 * `track()` (not `dispatch.analyticsScribe.trackButtonClicked`) because these
 * are new files where the rematch ratchet blocks new dispatch call sites; this
 * payload replicates the helper's exact routing — Snowplow + Datadog only,
 * `amplitude: false` is mandatory.
 */
export const trackNavV3ButtonClicked = (
  props: Omit<ButtonEventProps, 'feature'>
): void => {
  track({
    event_name: TrackingEventName.ButtonClicked,
    props: {
      screen_name: ScreenName.Sidenav,
      action: ButtonAction.NavV3ButtonClicked,
      ...props,
      feature: 'nav_v3',
    },
    options: { amplitude: false },
  });
};
